import { api } from './client'

export type Situacao = 'Verde' | 'Atencao' | 'Vermelho' | 'Desconhecida'

export type Indicador = {
  nome: string
  situacao: Situacao

  /** UI-09: a cor nunca carrega a informação sozinha — símbolo e rótulo vêm do back. */
  simbolo: string
  rotulo: string
  texto: string
  detalhe: string | null
}

export type Painel24h = {
  de: string
  ate: string
  situacaoGeral: Situacao
  sinalDeVida: Indicador[]
  volume: Record<string, number>
  transbordos: { motivo: string; total: number }[]
  custoDeIa: number
  falhasDeIa: number
  diasDeCreditoRestantes: number | null
  eventosFalhados: number
  enviosFalhados: number

  /**
   * Notas do copiloto nas 24 horas (demanda 056, critério 11).
   *
   * ⚠️ Não mede se a sugestão ajudou — mede que o copiloto falou. Sem nenhuma
   * medida, daqui a um mês ninguém sabe dizer se ele ajuda ou é enfeite caro.
   */
  notasDoCopiloto: number
}

export type Metricas = {
  de: string
  ate: string
  conversas: number
  mensagens: number

  /**
   * A evidência do nome do projeto. Sem número, "atendimento que nunca dorme"
   * é slogan; com número, é o argumento que vai para a Firjan.
   */
  conversasForaDoHorario: number
  percentualForaDoHorario: number

  transbordos: number
  percentualDeTransbordo: number

  /** Mediana, não média: uma conversa de sexta respondida na segunda distorce a média. */
  medianaDeSegundosAtePrimeiraResposta: number | null

  custo: number
  custoPorConversa: number
  porDia: { dia: string; conversas: number; mensagens: number }[]
  porCanal: { canal: string; marca: string; conversas: number; mensagens: number }[]
  porAssistente: { assistente: string; conversas: number; custo: number; porConversa: number }[]
  motivosDeTransbordo: { motivo: string; total: number }[]
}

/**
 * Como está um canal (ficha 311).
 *
 * 🔴 `NaoConferido` NÃO é `ForaDoAr`: é "não deu para perguntar". Uma consulta
 * que falhou não diz nada sobre o canal, e a tela não pode pintar de vermelho o
 * que só não respondeu.
 */
export type SituacaoDoCanal = 'EmOrdem' | 'Atencao' | 'ForaDoAr' | 'NaoConferido' | 'Desligado'

export type CanalDoPainel = {
  id: number
  idExterno: number
  nome: string
  marca: string
  assistente: string | null

  /** "WhatsApp", "Instagram", "Telegram", "Site"… — já em texto, do back. */
  tipo: string
  ativa: boolean
  telefone: string | null
  situacao: SituacaoDoCanal

  /** UI-09: a cor nunca carrega a informação sozinha — símbolo e rótulo vêm do back. */
  simbolo: string
  rotulo: string
  motivo: string
  oQueFazer: string | null
  ultimaRecebidaEm: string | null
  ultimaEnviadaEm: string | null

  /** Nulo em canal sem conferência junto ao provedor (Instagram, Telegram, site). */
  conferidoEm: string | null
  provedor: {
    status: string | null
    plataforma: string | null
    podeEnviar: string | null
    appInscrito: boolean | null
    entregaConfere: boolean | null
    respondidoEm: string | null
  } | null

  /** `naoEnviado` é o motivo de o e-mail do alerta não ter saído. */
  alerta: {
    tipo: 'Nenhum' | 'ForaDoAr' | 'SemConferencia'
    desde: string | null
    ultimoAvisoEm: string | null
    naoEnviado: string | null
  } | null
}

export type CanaisDoPainel = {
  em: string
  alertaLigado: boolean
  horasSemReceber: number

  /** Só na resposta do "conferir agora": falso = já havia uma conferência em andamento. */
  conferiu: boolean | null
  canais: CanalDoPainel[]
}

export const painel = {
  canais: () => api.get<CanaisDoPainel>('/painel/canais'),
  conferirCanais: () => api.post<CanaisDoPainel>('/painel/canais/conferir', {}),
  vinteQuatroHoras: () => api.get<Painel24h>('/painel/24h'),
  metricas: (dias = 30) => api.get<Metricas>(`/painel/metricas?dias=${dias}`),
  enviarResumo: () => api.post<{ enviado: boolean }>('/painel/24h/enviar', {}),
}
